import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, orderBy, query, updateDoc, serverTimestamp } from 'firebase/firestore';
import { AlertOctagon, AlertTriangle, Info, Plus } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_SQUAWKS, lesseeByUid } from '../../lib/demoData';
import type { SquawkDoc, SquawkSeverity, SquawkStatus } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { FilterChip } from '../../components/FilterChip';
import { SQUAWK_SEV_LABEL, SQUAWK_SEV_RANK } from '../../lib/squawks';
import { TRACKED_REG } from '../../lib/flightTrack';

const SEV_ICON: Record<SquawkSeverity, React.ReactNode> = {
  informational: <Info size={16} className="text-sky-600" />,
  should_fix_soon: <AlertTriangle size={16} className="text-amber-600" />,
  grounding: <AlertOctagon size={16} className="text-red-600" />,
};

const STATUS_OPTIONS: SquawkStatus[] = [
  'open',
  'acknowledged',
  'in_progress',
  'resolved',
  'cannot_reproduce',
  'not_an_issue',
];

function titleCase(s: string) {
  const spaced = s.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// A squawk is "closed" (history) once it's resolved or dismissed; everything else
// is still open and needs attention.
const CLOSED_STATUSES: SquawkStatus[] = ['resolved', 'cannot_reproduce', 'not_an_issue'];
type SquawkFilter = 'open' | 'history' | 'all';

export function AdminSquawks() {
  const { demoMode } = useAuth();
  const [squawks, setSquawks] = useState<SquawkDoc[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter, setFilter] = useState<SquawkFilter>('open');

  useEffect(() => {
    if (demoMode) {
      setSquawks(DEMO_SQUAWKS);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'squawks'), orderBy('createdAt', 'desc')),
      (snap) => setSquawks(snap.docs.map((d) => d.data() as SquawkDoc))
    );
    return () => unsub();
  }, [demoMode]);

  async function patch(s: SquawkDoc, p: Partial<SquawkDoc>) {
    if (demoMode) {
      setSquawks((curr) => (curr ?? []).map((x) => (x.squawkId === s.squawkId ? { ...x, ...p } : x)));
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'squawks', s.squawkId), {
      ...p,
      ...(p.status === 'resolved' ? { resolvedAt: serverTimestamp() } : {}),
    });
  }

  const all = squawks ?? [];
  const openCount = all.filter((s) => !CLOSED_STATUSES.includes(s.status)).length;
  const historyCount = all.filter((s) => CLOSED_STATUSES.includes(s.status)).length;

  const sorted = all
    .filter((s) => {
      if (filter === 'all') return true;
      const closed = CLOSED_STATUSES.includes(s.status);
      return filter === 'history' ? closed : !closed;
    })
    .slice()
    .sort((a, b) => {
      // Open list: worst severity first. History: most recently resolved first.
      if (filter === 'history') {
        const at = (a.resolvedAt ?? a.createdAt).toDate().getTime();
        const bt = (b.resolvedAt ?? b.createdAt).toDate().getTime();
        return bt - at;
      }
      if (SQUAWK_SEV_RANK[a.severity] !== SQUAWK_SEV_RANK[b.severity]) return SQUAWK_SEV_RANK[a.severity] - SQUAWK_SEV_RANK[b.severity];
      return a.createdAt.toDate().getTime() < b.createdAt.toDate().getTime() ? 1 : -1;
    });

  const subtitle =
    filter === 'history'
      ? `Resolved & closed squawks for ${TRACKED_REG}. Most recent first.`
      : filter === 'all'
      ? `Every squawk for ${TRACKED_REG}, open and closed.`
      : `Open issues for ${TRACKED_REG}. Sorted by severity.`;

  const emptyMsg =
    filter === 'history'
      ? 'No resolved squawks yet.'
      : filter === 'open'
      ? 'No open squawks — the airplane is clean.'
      : 'No squawks reported.';

  return (
    <>
      <PageHeader
        title="Squawks"
        subtitle={subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Link to="/admin/squawks/new" className="btn-primary inline-flex items-center gap-1.5 py-1.5 px-3">
              <Plus size={15} /> Add squawk
            </Link>
            <FilterChip active={filter === 'open'} onClick={() => setFilter('open')}>
              Open{openCount > 0 ? ` · ${openCount}` : ''}
            </FilterChip>
            <FilterChip active={filter === 'history'} onClick={() => setFilter('history')}>
              History{historyCount > 0 ? ` · ${historyCount}` : ''}
            </FilterChip>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              All
            </FilterChip>
          </div>
        }
      />
      {!squawks ? null : sorted.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">{emptyMsg}</div>
      ) : (
        <div className="space-y-3">
          {sorted.map((s) => {
            const u = lesseeByUid(s.reportedBy);
            const reporter =
              s.reportedByName ?? [u?.personal?.firstName, u?.personal?.lastName].filter(Boolean).join(' ');
            const isOpen = expanded === s.squawkId;
            return (
              <div key={s.squawkId} className="card p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                  <div className="flex items-center gap-2">
                    {SEV_ICON[s.severity]}
                    <span className="text-sm font-medium text-navy-900">{SQUAWK_SEV_LABEL[s.severity]}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="label sr-only" htmlFor={`status-${s.squawkId}`}>
                      Status
                    </label>
                    <select
                      id={`status-${s.squawkId}`}
                      className="input py-1.5 px-2 text-xs min-h-0 w-auto"
                      value={s.status}
                      onChange={(e) => patch(s, { status: e.target.value as SquawkStatus })}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {titleCase(o)}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setExpanded(isOpen ? null : s.squawkId)}
                      className="text-navy-700 underline text-xs"
                    >
                      {isOpen ? 'Hide' : 'Details'}
                    </button>
                  </div>
                </div>

                <div className="mt-2 text-xs text-ink-muted">
                  <span className="font-medium text-navy-800">
                    {reporter}
                  </span>
                  {' · '}
                  {s.dateDiscovered}
                </div>

                <div className="mt-2 whitespace-pre-wrap break-words text-sm text-ink">{s.description}</div>

                {isOpen && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <label className="label" htmlFor={`notes-${s.squawkId}`}>
                      Internal notes (admin-only)
                    </label>
                    <textarea
                      id={`notes-${s.squawkId}`}
                      className="input-textarea text-sm"
                      defaultValue={s.adminNotes ?? ''}
                      onBlur={(e) => patch(s, { adminNotes: e.target.value })}
                      placeholder="Called the shop, scheduled Tuesday…"
                    />
                    <div className="mt-3">
                      <label className="inline-flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={s.isGrounding}
                          onChange={(e) => patch(s, { isGrounding: e.target.checked })}
                        />
                        <span>Grounding (blocks bookings + SMS-alerts all active lessees in Slice 3)</span>
                      </label>
                    </div>
                    {s.isGrounding && (
                      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs font-medium text-navy-900">Billing fault</p>
                        <p className="mt-0.5 text-xs text-ink-muted">
                          No-fault grounding prorates lessees’ monthly minimum (they get a credit).
                          Mark lessee-caused and it bills full. Editable anytime.
                        </p>
                        <div className="mt-2 inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                          {(['no_fault', 'lessee_caused'] as const).map((f) => {
                            const active = (s.groundingFault ?? 'no_fault') === f;
                            return (
                              <button
                                key={f}
                                type="button"
                                onClick={() => patch(s, { groundingFault: f })}
                                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                                  active ? 'bg-brand text-white' : 'text-ink-muted hover:text-navy-900'
                                }`}
                              >
                                {f === 'no_fault' ? 'No fault (credit)' : 'Lessee-caused (bill full)'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
